import { useEffect } from "react";
import { Link, usePage } from "@inertiajs/react";
import Offcanvas from "bootstrap/js/dist/offcanvas";
import ProfileDrawerForm from "@/components/profile-drawer-form";
import { useDrawer } from "@/components/drawer-provider";
import { chatbot_chats_path, documents_path, guides_path, manage_chatbot_prompt_instruction_path, manage_organizations_path, manage_products_path, manage_users_path, organization_files_path, products_path, registration_path, session_path, support_path, switch_organization_path, templates_path, users_path } from "@/routes";

function cleanupOffcanvasArtifacts(sidebarElement) {
  sidebarElement?.classList.remove("show", "showing", "hiding");
  sidebarElement?.removeAttribute("aria-modal");
  sidebarElement?.removeAttribute("role");
  sidebarElement?.setAttribute("aria-hidden", "true");

  document.body.classList.remove("offcanvas-backdrop", "modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");

  document.querySelectorAll(".offcanvas-backdrop").forEach((backdrop) => backdrop.remove());
}

function buildWorkspaceNavigationItems({ currentTenant, currentUser }) {
  const items = [
    {
      label: "Produkter",
      href: products_path(),
      matches: ["products/", "steps/"],
      type: "link"
    }
  ];

  if (currentUser?.can_access_content_library) {
    items.push({ label: "Dokumenter", href: documents_path(), contentKind: "document", type: "link" });
  }

  if (currentTenant?.id) {
    items.push({
      label: "Filer",
      href: organization_files_path(),
      matches: ["organization-files/"],
      type: "link"
    });
  }

  if (currentTenant?.id && currentUser?.chatbot_available) {
    items.push({
      label: "Chat",
      href: chatbot_chats_path(),
      matches: ["chatbot/chats"],
      type: "link"
    });
  }

  if (currentTenant?.id && currentUser?.can_access_users) {
    items.push({
      label: "Brugere",
      href: users_path(),
      matches: ["users/", "invitations/"],
      type: "link"
    });
  }

  items.push({
    label: "Support",
    href: support_path(),
    matches: ["support/"],
    type: "link"
  });

  return items;
}

function buildManageNavigationItems({ currentUser }) {
  if (!currentUser?.super_admin) {
    return [];
  }

  return [
    {
      label: "Alle produkter",
      href: manage_products_path(),
      matches: ["manage/products/"],
      type: "link"
    },
    {
      label: "Alle organisationer",
      href: manage_organizations_path(),
      matches: ["manage/organizations/", "organizations/"],
      type: "link"
    },
    {
      label: "Alle brugere",
      href: manage_users_path(),
      matches: ["manage/users/"],
      type: "link"
    },
    {
      label: "Alle skabeloner",
      href: templates_path(),
      contentKind: "template",
      type: "link"
    },
    {
      label: "Alle guides",
      href: guides_path(),
      contentKind: "guide",
      type: "link"
    },
    {
      label: "Chat instruktioner",
      href: manage_chatbot_prompt_instruction_path(),
      matches: ["manage/chatbot/prompt-instructions/"],
      type: "link"
    }
  ];
}

function initialsFor(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "O";
}

function matchesComponent(component, contentKind, item) {
  if (!component) return false;

  if (item.contentKind) return ["content-library/index", "content-library/edit", "content-library/show"].includes(component) ? contentKind === item.contentKind : false;

  if (Array.isArray(item.matches) && item.matches.length > 0) {
    return item.matches.some((match) => component.startsWith(match));
  }

  if (item.match) {
    return component.startsWith(item.match);
  }

  return false;
}

function SidebarNav({ component, contentKind, currentTenant, currentUser, availableOrganizations }) {
  const { openDrawer, closeDrawer } = useDrawer();
  const workspaceNavigationItems = buildWorkspaceNavigationItems({ currentTenant, currentUser });
  const workspaceName = currentTenant?.name || "Obsero Core";
  const workspaceInitials = initialsFor(workspaceName);

  const openProfileDrawer = () => {
    openDrawer({
      title: "Profil",
      content: (
        <ProfileDrawerForm
          currentUser={currentUser}
          updatePath={registration_path()}
          onSuccess={closeDrawer}
        />
      ),
      footer: (
        <button className="btn btn-primary rounded-3 px-4" type="submit" form="profile-drawer-form">
          Gem profil
        </button>
      ),
    });
  };

  const manageNavigationItems = buildManageNavigationItems({ currentUser });

  const renderNavigationItem = (item) => {
    const isActive = matchesComponent(component, contentKind, item);
    const classes = [
      "auth-sidebar__link",
      isActive ? "is-active" : "",
      item.type === "static" ? "is-static" : ""
    ].filter(Boolean).join(" ");

    const content = (
      <>
        <span>{item.label}</span>
      </>
    );

    if (item.type === "link") {
      return (
        <Link key={item.label} href={item.href} className={classes}>
          {content}
        </Link>
      );
    }

    return (
      <div key={item.label} className={classes} role="link" tabIndex={0} aria-disabled="true">
        {content}
      </div>
    );
  };

  return (
    <>
      <div className="auth-sidebar__brand-wrap">
        <Link href={products_path()} className="public-brand public-brand--footer auth-sidebar__brand">
          <span className="public-brand__copy">
            <strong>obsero</strong>
          </span>
        </Link>
      </div>

      <div className="auth-sidebar__body">
        <div className="auth-sidebar__scroll">
          <div className="auth-sidebar__section">
            <p className="auth-sidebar__section-label">Arbejdsrum</p>
            <nav className="auth-sidebar__nav">
              {workspaceNavigationItems.map(renderNavigationItem)}
            </nav>
          </div>

          {manageNavigationItems.length > 0 ? (
            <>
              <div className="auth-sidebar__divider" />

              <div className="auth-sidebar__section mt-4">
                <p className="auth-sidebar__section-label">Administration</p>
                <nav className="auth-sidebar__nav">
                  {manageNavigationItems.map(renderNavigationItem)}
                </nav>
              </div>
            </>
          ) : null}
        </div>

        <div className="auth-sidebar__footer">
          <div className="dropdown">
            <button
              type="button"
              className="auth-sidebar__account auth-sidebar__account-button dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <span className="auth-sidebar__account-avatar">{workspaceInitials}</span>
              <span className="auth-sidebar__account-copy">
                <strong>{workspaceName}</strong>
                <small>Virksomhedskonto</small>
              </span>
            </button>

            <div className="dropdown-menu dropdown-menu-end auth-sidebar__account-menu">
              <div className="auth-sidebar__account-menu-label">Skift organisation</div>

              {availableOrganizations.length > 0 ? (
                availableOrganizations.map((organization) => (
                  <Link
                    key={organization.id}
                    href={switch_organization_path(organization.id)}
                    method="patch"
                    as="button"
                    className={`dropdown-item auth-sidebar__account-option ${organization.id === currentTenant?.id ? "active" : ""}`}
                  >
                    <span>{organization.name}</span>
                    {organization.id === currentTenant?.id ? (
                      <span className="auth-sidebar__account-option-badge">Aktiv</span>
                    ) : null}
                  </Link>
                ))
              ) : (
                <button className="dropdown-item auth-sidebar__account-option" type="button" disabled>
                  Ingen organisationer
                </button>
              )}

              <div className="dropdown-divider auth-sidebar__account-divider" />

              <button className="dropdown-item auth-sidebar__account-option" onClick={openProfileDrawer}>
                Kontoindstillinger
              </button>

              <Link
                href={session_path()}
                method="delete"
                as="button"
                className="dropdown-item auth-sidebar__account-option"
              >
                Log ud
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function Sidebar() {
  const { component, props } = usePage();
  const {
    current_tenant: currentTenant,
    current_user: currentUser,
    available_organizations: availableOrganizations = []
  } = props || {};

  useEffect(() => {
    const sidebarElement = document.getElementById("authSidebar");
    if (!sidebarElement || typeof window === "undefined") return;

    const desktopQuery = window.matchMedia("(min-width: 992px)");
    const closeSidebarOnDesktop = () => {
      if (!desktopQuery.matches) return;

      Offcanvas.getOrCreateInstance(sidebarElement).hide();
      window.setTimeout(() => cleanupOffcanvasArtifacts(sidebarElement), 250);
      cleanupOffcanvasArtifacts(sidebarElement);
    };

    closeSidebarOnDesktop();

    if (typeof desktopQuery.addEventListener === "function") {
      desktopQuery.addEventListener("change", closeSidebarOnDesktop);
      return () => {
        desktopQuery.removeEventListener("change", closeSidebarOnDesktop);
        cleanupOffcanvasArtifacts(sidebarElement);
      };
    }

    desktopQuery.addListener(closeSidebarOnDesktop);
    return () => {
      desktopQuery.removeListener(closeSidebarOnDesktop);
      cleanupOffcanvasArtifacts(sidebarElement);
    };
  }, []);

  return (
    <>
      <aside className="auth-sidebar d-none d-lg-flex">
        <SidebarNav
          component={component}
          contentKind={props?.content_kind}
          currentTenant={currentTenant}
          currentUser={currentUser}
          availableOrganizations={availableOrganizations}
        />
      </aside>

      <div
        className="offcanvas offcanvas-start auth-sidebar-offcanvas"
        tabIndex="-1"
        id="authSidebar"
        aria-labelledby="authSidebarLabel"
      >
        <div className="offcanvas-header border-bottom">
          <h2 className="offcanvas-title h5 mb-0" id="authSidebarLabel">
            Obsero
          </h2>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Luk"
          />
        </div>
        <div className="offcanvas-body p-0 d-flex flex-column">
          <SidebarNav
            component={component}
            contentKind={props?.content_kind}
            currentTenant={currentTenant}
            currentUser={currentUser}
            availableOrganizations={availableOrganizations}
          />
        </div>
      </div>
    </>
  );
}
